import { useCallback, useEffect, useRef } from 'react';

import { triggerDownloadWithAnchorTag } from '../../utils/triggerDownloadWithAnchorTag';

import { getPdfSourceKey, toPdfBlob } from './pdfSource';
import type { PdfDocumentSource } from './types';

import sharedStyles from './documentPreviewShared.module.scss';

type UsePdfDocumentActionsOptions = {
  source: PdfDocumentSource | null;
  filename: string;
};

export function usePdfDocumentActions({
  source,
  filename,
}: UsePdfDocumentActionsOptions) {
  const blobUrlRef = useRef<string | null>(null);
  const sourceKey = source ? getPdfSourceKey(source) : null;

  const cleanupBlobUrl = useCallback(() => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  useEffect(() => cleanupBlobUrl, [cleanupBlobUrl, sourceKey]);

  const ensureActionUrl = useCallback((): string => {
    if (!source) {
      return '';
    }
    if (source.type === 'url') {
      return source.url;
    }
    if (!blobUrlRef.current) {
      const blob = toPdfBlob(source);
      if (blob) {
        blobUrlRef.current = URL.createObjectURL(blob);
      }
    }
    return blobUrlRef.current ?? '';
  }, [source]);

  const download = useCallback(() => {
    const url = ensureActionUrl();
    if (!url) {
      return;
    }
    triggerDownloadWithAnchorTag(url, filename);
  }, [ensureActionUrl, filename]);

  const print = useCallback(() => {
    const url = ensureActionUrl();
    if (!url) {
      return;
    }
    const frame = globalThis.document.createElement('iframe');
    frame.className = sharedStyles['print-frame'];
    frame.src = url;
    frame.addEventListener('load', () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      globalThis.setTimeout(() => frame.remove(), 1000);
    });
    globalThis.document.body.append(frame);
  }, [ensureActionUrl]);

  return { download, print };
}
